import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchAccountHeads } from "../../redux/slices/accountHeadSlice";
import { createJournalEntryThunk, updateJournalEntryThunk } from "../../redux/slices/financeSlice";

const today = () => new Date().toISOString().split("T")[0];

const EMPTY_LINE = { accountHeadId: "", description: "", debitAmount: 0, creditAmount: 0 };

const EMPTY = {
  journalDate: today(),
  reference: "",
  narration: "",
  lines: [{ ...EMPTY_LINE }, { ...EMPTY_LINE }],
};

const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

const JournalModal = ({ show, onClose, editRecord }) => {
  const dispatch = useDispatch();
  const accountHeads = useSelector((s) => s.accountHeads.list);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const headOptions = accountHeads
    .filter((h) => h.status === "Active")
    .map((h) => ({ value: h.accountID || h.id, label: h.accountName }));

  useEffect(() => {
    dispatch(fetchAccountHeads());
  }, [dispatch]);

  useEffect(() => {
    if (editRecord) {
      setForm({
        ...EMPTY,
        ...editRecord,
        lines: editRecord.lines?.length ? editRecord.lines.map((l) => ({ ...EMPTY_LINE, ...l })) : [{ ...EMPTY_LINE }, { ...EMPTY_LINE }],
      });
    } else {
      setForm({ ...EMPTY, journalDate: today(), lines: [{ ...EMPTY_LINE }, { ...EMPTY_LINE }] });
    }
  }, [editRecord, show]);

  const setHeader = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const updateLine = (idx, field, value) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...f, lines };
    });
  };

  const selectHead = (idx, opt) => {
    setForm((f) => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], accountHeadId: opt?.value || "" };
      return { ...f, lines };
    });
  };

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { ...EMPTY_LINE }] }));
  const removeLine = (idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const totalDebit = form.lines.reduce((s, l) => s + Number(l.debitAmount || 0), 0);
  const totalCredit = form.lines.reduce((s, l) => s + Number(l.creditAmount || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.journalDate) {
      Swal.fire("Required", "Journal date is required.", "warning");
      return;
    }
    if (!isBalanced) {
      Swal.fire("Unbalanced", `Debit (₹${fmt(totalDebit)}) and Credit (₹${fmt(totalCredit)}) totals must match.`, "warning");
      return;
    }
    const validLines = form.lines.filter((l) => l.accountHeadId && (Number(l.debitAmount) > 0 || Number(l.creditAmount) > 0));
    if (validLines.length < 2) {
      Swal.fire("Required", "At least 2 journal lines are required.", "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, lines: validLines };
      if (editRecord?.id) {
        await dispatch(updateJournalEntryThunk({ id: editRecord.id, data: payload })).unwrap();
      } else {
        await dispatch(createJournalEntryThunk(payload)).unwrap();
      }
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Journal entry saved", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch {
      Swal.fire("Error", "Failed to save journal entry.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{editRecord ? "Edit Journal Entry" : "New Journal Entry"}</h4>
              </div>
              <button type="button" className="close" onClick={onClose}><span>×</span></button>
            </div>
            <div className="modal-body custom-modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-lg-3">
                    <label className="form-label">Date <span className="text-danger">*</span></label>
                    <input type="date" className="form-control" value={form.journalDate} onChange={setHeader("journalDate")} />
                  </div>
                  <div className="col-lg-3">
                    <label className="form-label">Reference No.</label>
                    <input className="form-control" value={form.reference} onChange={setHeader("reference")} placeholder="JV-001" />
                  </div>
                  <div className="col-lg-6">
                    <label className="form-label">Narration</label>
                    <input className="form-control" value={form.narration} onChange={setHeader("narration")} placeholder="Brief description of this entry" />
                  </div>
                </div>

                <div className="table-responsive mb-2">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: 200 }}>Account Head</th>
                        <th>Description</th>
                        <th style={{ width: 130 }} className="text-end">Debit (₹)</th>
                        <th style={{ width: 130 }} className="text-end">Credit (₹)</th>
                        <th style={{ width: 50 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.lines.map((line, idx) => (
                        <tr key={idx}>
                          <td>
                            <Select
                              classNamePrefix="react-select"
                              options={headOptions}
                              value={headOptions.find((o) => o.value === line.accountHeadId) || null}
                              onChange={(opt) => selectHead(idx, opt)}
                              placeholder="Select..."
                              menuPosition="fixed"
                              styles={{ menu: (b) => ({ ...b, zIndex: 9999 }) }}
                            />
                          </td>
                          <td>
                            <input className="form-control form-control-sm" value={line.description}
                              onChange={(e) => updateLine(idx, "description", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                              value={line.debitAmount}
                              onChange={(e) => updateLine(idx, "debitAmount", e.target.value)} />
                          </td>
                          <td>
                            <input type="number" className="form-control form-control-sm text-end" min={0} step="0.01"
                              value={line.creditAmount}
                              onChange={(e) => updateLine(idx, "creditAmount", e.target.value)} />
                          </td>
                          <td className="text-center align-middle">
                            {form.lines.length > 2 && (
                              <button type="button" className="btn btn-sm text-danger p-1" onClick={() => removeLine(idx)}>×</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className={isBalanced ? "table-success" : "table-danger"}>
                        <td colSpan={2} className="text-end fw-bold">
                          Total {!isBalanced && <span className="text-danger small ms-2">⚠ Not Balanced</span>}
                        </td>
                        <td className="text-end fw-bold">₹{fmt(totalDebit)}</td>
                        <td className="text-end fw-bold">₹{fmt(totalCredit)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <button type="button" className="btn btn-sm btn-outline-primary mb-3" onClick={addLine}>+ Add Line</button>

                <div className="modal-footer-btn">
                  <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                  <button type="submit" className="btn btn-submit" disabled={saving}>
                    {saving ? "Saving..." : editRecord ? "Save Changes" : "Post Entry"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default JournalModal;
